import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { graphql } from 'react-apollo';
import { withRouter } from 'react-router-dom';
import {
  Message, Confirm, Table, Dimmer, Loader,
} from 'semantic-ui-react';
import { isEmpty } from 'lodash';
import { connect } from 'react-redux';

import Search from '../views/Shared/Search';
import withPagination from './WithPagination';
import LoadingDataEnhancer from './LoadingData';
import ReturnInvoicesList from '../views/ReturnInvoices/List';
import ReturnInvoiceCancel from '../views/ReturnInvoices/CancelForm';
import ReturnInvoiceEdit from '../views/ReturnInvoices/Edit';

import returnInvoicesQuery from '../graphql/returnInvoicesQuery';
import cancelReturnInvoiceMutation from '../graphql/cancelReturnInvoiceMutation';
import updateReturnInvoiceMutation from '../graphql/updateReturnInvoiceMutation';
import resendVraStatusMutation from '../graphql/resendVraStatusMutation';
import { clearSearch, setSearch } from '../data/searchField/actions';
import { setRefreshSearch } from '../data/refreshSearch/actions';
import APP_PATH from '../constants/paths';
import { PRODUCT_FAMILY_ALL } from '../constants/productFamily';
import { setPage } from '../data/page/actions';
import AuditModal from '../components/AuditModal';
import returnInvoiceAuditsQuery from '../graphql/returnInvoiceAuditsQuery';
import { selectReturnInvoicesProductFamily } from '../data/returnInvoices/selectors';

const LIST_LOAD_ERROR = 'Could not load return invoices. Change the product or search again to retry.';

class ReturnInvoices extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedReturnInvoice: null,
      isCancelModalOpen: false,
      resendFormError: null,
      cancelFormError: null,
      cancelFormSubmitting: null,
      flashMessage: null,
      isAuditModalOpen: false,
      isConfirmationOpen: false,
      isEditModalOpen: false,
      editFormError: null,
      editFormSubmitting: null,
      isNegativeFlashMessage: false,
      isProductFamilyLoading: false,
      listLoadFailed: false,
    };
    this.requestedProductFamily = props.data.variables.productFamily;
    this.productFamilyRequestId = 0;

    const {
      clearSearchFields,
      dispatchRefreshSearch,
      refreshSearch,
      data,
    } = props;

    if (refreshSearch) {
      dispatchRefreshSearch(false);
      clearSearchFields({});
      const variableArguments = {
        vin: null,
        dealerNumbers: [],
      };
      data.refetch({
        ...variableArguments,
      });
    }
  }

  componentDidMount() {
    const { searchFields, data, refreshSearch } = this.props;
    // On re-navigation, WithPagination always starts with empty queryVars.
    // If Redux still holds a previous search, re-fire the query with those vars
    // so the displayed data matches the search inputs.
    if (!refreshSearch && searchFields && Object.keys(searchFields).length > 0) {
      data.refetch(searchFields);
    }
    this.syncProductFamily();
  }

  componentDidUpdate() {
    this.syncProductFamily();
  }

  syncProductFamily = () => {
    const { productFamily } = this.props;
    if (productFamily !== this.requestedProductFamily) {
      this.requestedProductFamily = productFamily;
      this.refetchForProductFamily(productFamily);
    }
  };

  refetchForProductFamily = productFamily => {
    const { data, setPage: setPageProp } = this.props;
    this.productFamilyRequestId += 1;
    const requestId = this.productFamilyRequestId;
    const isLatest = () => requestId === this.productFamilyRequestId;
    this.setState({ isProductFamilyLoading: true, listLoadFailed: false });
    data.refetch({ productFamily })
      .then(() => {
        if (!isLatest()) return;
        this.setState({ listLoadFailed: false });
        setPageProp({});
      })
      .catch(() => {
        if (isLatest()) this.setState({ listLoadFailed: true });
      })
      .finally(() => {
        if (isLatest()) this.setState({ isProductFamilyLoading: false });
      });
  };

  handleDismiss = () => {
    this.setState({
      flashMessage: null,
      editFormError: null,
    });
  };

  onSelectReturnInvoice = (action, returnInvoice) => {
    this.setState({
      selectedReturnInvoice: returnInvoice,
    });

    const { history } = this.props;
    switch (action) {
      case 'Edit':
        this.setState({
          editFormError: null,
          isCancelModalOpen: false,
          editFormSubmitting: null,
          isEditModalOpen: true,
        });
        break;
      case 'Cancel Return':
        this.setState({
          isCancelModalOpen: true,
          cancelFormError: null,
          cancelFormSubmitting: null,
          isEditModalOpen: false,
        });
        break;
      case 'Vehicle Status Tracker':
        history.push(
          APP_PATH.VEHICLE_STATUS_TRACKER.replace(
            ':purchaseId',
            returnInvoice.guaranteePurchaseId
          )
        );
        break;
      case 'Audit':
        this.setState({
          isAuditModalOpen: true,
          isEditModalOpen: false,
          cancelFormError: null,
          cancelFormSubmitting: null,
        });
        break;
      case 'Resend VRA status':
        this.setState({
          isConfirmationOpen: true,
          resendFormError: null,
          isEditModalOpen: false,
        });
        break;
      default:
        this.setState({
          selectedReturnInvoice: null,
        });
    }
  };

  onCancelFormCloseHandler = () => {
    this.setState({
      isCancelModalOpen: false,
      selectedReturnInvoice: null,
    });
  };

  onEditFormCloseHandler = () => {
    this.setState({
      isEditModalOpen: false,
      selectedReturnInvoice: null,
    });
  };

  onAuditCloseHandler = () => {
    this.setState({
      isAuditModalOpen: false,
    });
  };

  handleConfirmationClose = () => {
    this.setState({ isConfirmationOpen: false });
  };

  handleConfirm = () => {
    const {
      selectedReturnInvoice: { id },
    } = this.state;
    const { resendVraMutation } = this.props;
    const input = { returnInvoiceId: id };
    resendVraMutation({
      variables: { input },
    })
      .then(() => {
        this.setState({
          isConfirmationOpen: false,
          resendFormError: null,
          flashMessage: 'Successfully Resend VRA status',
        });
      })
      .catch(() => {
        this.setState({
          isConfirmationOpen: false,
          resendFormError: 'Error while Resending VRA status',
        });
      });
  };

  onCancelFormSubmitHandler = values => {
    this.setState({
      cancelFormSubmitting: true,
    });
    const { cancelMutation, data } = this.props;
    const cancelInput = { ...values, productFamily: PRODUCT_FAMILY_ALL };
    cancelMutation({
      variables: { cancelInput },
    })
      .then(({ data: responseData }) => {
        const {
          cancelReturnInvoice: { success, errors },
        } = responseData;
        this.setState({
          isCancelModalOpen: false,
          selectedReturnInvoice: null,
          cancelFormSubmitting: null,
          flashMessage: success
            ? 'Successfully Cancelled Return Invoice'
            : errors,
          isNegativeFlashMessage: !success,
        });
        data.fetchMore({
          updateQuery: (
            previousResult,
            { fetchMoreResult }
          ) => {
            return fetchMoreResult;
          },
        });
      })
      .catch(() => {
        this.setState({
          cancelFormSubmitting: false,
          cancelFormError: 'Error while cancelling Return Invoice',
        });
      });
  };

  onEditFormSubmitHandler = values => {
    this.setState({
      editFormSubmitting: true,
    });
    const { editMutation, data } = this.props;
    const input = { returnInvoice: { ...values }, productFamily: PRODUCT_FAMILY_ALL };
    editMutation({
      variables: { input },
    })
      .then(({ data: responseData }) => {
        const {
          updateReturnInvoice: { success, errors },
        } = responseData;
        if (success) {
          data.refetch().then(() => {
            this.setState({
              isEditModalOpen: false,
              selectedReturnInvoice: null,
              editFormSubmitting: null,
              flashMessage: 'Successfully Edited Return Invoice',
            });
          }).catch(() => {
            this.setState({
              isEditModalOpen: false,
              selectedReturnInvoice: null,
              editFormSubmitting: null,
              flashMessage: 'Successfully Edited Return Invoice',
            });
          });
        } else {
          this.setState({
            editFormSubmitting: false,
            editFormError: errors,
          });
          this.onEditFormCloseHandler();
        }
      })
      .catch(() => {
        this.setState({
          editFormSubmitting: false,
          editFormError: 'Error updating return invoice',
        });
        this.onEditFormCloseHandler();
      });
  };

  onSearchSubmitHandler = event => {
    const {
      data,
      setSearchFields,
      setPage: setPageProp,
    } = this.props;
    const { vin, dealerNumbers, vra } = event.target;
    const dealerNos = isEmpty(dealerNumbers.value)
      ? null
      : [dealerNumbers.value.trim()];
    const variableArguments = {
      vin: vin.value.trim() || null,
      dealerNumbers: dealerNos,
      vra: vra.value || null,
    };
    data.refetch({
      ...variableArguments,
    })
      .then(() => this.setState({ listLoadFailed: false }))
      .catch(() => this.setState({ listLoadFailed: true }));

    setSearchFields(variableArguments);
    setPageProp({});
  };

  render() {
    const renderAuditRow = audit => {
      return (
        <>
          {audit.event === 'update'
            && `${audit.versionAuthor} updated the Return invoice - ${
              audit.itemType
            } details`}
          {audit.event === 'create'
            && `${audit.versionAuthor} created the Return invoice - ${
              audit.itemType
            }`}
          {audit.event === 'destroy'
            && `${audit.versionAuthor} Destroyed Return invoice - ${
              audit.itemType
            }`}
        </>
      );
    };

    const renderChangesetRow = (auditRecord, change, key) => {
      if (key !== 'updated_at'
        && auditRecord.event === 'update'
        && change[1] !== null
        && change[1] !== '') {
        return (
          <Table.Row key={key}>
            <Table.Cell>
              <span>
                set{' '}
                <span style={{ fontWeight: 'bold' }}>
                  {key}{' '}
                </span>
                from{' '}
                <span style={{ fontWeight: 'bold' }}>
                  {' '}
                  {change[0] || 'nil'}{' '}
                </span>
                to{' '}
                <span style={{ fontWeight: 'bold' }}>
                  {change[1] || 'nil'}{' '}
                </span>
              </span>
            </Table.Cell>
          </Table.Row>
        );
      }
      return null;
    };

    const {
      data: { returnInvoices },
      searchFields,
      user,
    } = this.props;
    const {
      selectedReturnInvoice,
      isCancelModalOpen,
      isAuditModalOpen,
      isEditModalOpen,
      resendFormError,
      cancelFormError,
      editFormError,
      cancelFormSubmitting,
      flashMessage,
      editFormSubmitting,
      isNegativeFlashMessage,
      isConfirmationOpen,
      isProductFamilyLoading,
      listLoadFailed,
    } = this.state;

    return (
      <div>
        {flashMessage && (
          <Message
            onDismiss={this.handleDismiss}
            content={flashMessage}
            positive
            negative={isNegativeFlashMessage}
          />
        )}
        {editFormError && (
          <Message
            onDismiss={this.handleDismiss}
            content={editFormError}
            negative
          />
        )}
        <Search
          onSubmit={this.onSearchSubmitHandler}
          searchFields={searchFields}
          searchOptions={['vin', 'dealerNumbers', 'vra']}
        />
        <Dimmer.Dimmable dimmed={isProductFamilyLoading}>
          <Dimmer active={isProductFamilyLoading} inverted>
            <Loader data-testid='list-loading-overlay'>Loading...</Loader>
          </Dimmer>
          {listLoadFailed ? (
            <Message negative data-testid='list-load-error' content={LIST_LOAD_ERROR} />
          ) : (
            <ReturnInvoicesList
              onSelectReturnInvoice={this.onSelectReturnInvoice}
              returnInvoices={returnInvoices}
              user={user}
            />
          )}
        </Dimmer.Dimmable>
        {selectedReturnInvoice && isAuditModalOpen && (
          <div>
            <AuditModal
              header='Status History - Return Invoices'
              fetchAuditRecordQuery={returnInvoiceAuditsQuery}
              queryVariables={{ returnInvoiceId: parseInt(selectedReturnInvoice.id, 10) }}
              auditResponseAttribute='returnInvoiceAudits'
              renderAuditRow={renderAuditRow}
              renderChangesetRow={renderChangesetRow}
              isAuditModalOpen={isAuditModalOpen}
              onCloseHandler={this.onAuditCloseHandler}
            />
          </div>
        )}
        {selectedReturnInvoice && isCancelModalOpen && (
          <div>
            <ReturnInvoiceCancel
              isCancelModalOpen={isCancelModalOpen}
              onCloseHandler={this.onCancelFormCloseHandler}
              onSubmitHandler={this.onCancelFormSubmitHandler}
              returnInvoice={selectedReturnInvoice}
              error={cancelFormError}
              submitting={cancelFormSubmitting}
            />
          </div>
        )}
        {selectedReturnInvoice && isEditModalOpen && (
          <div>
            <ReturnInvoiceEdit
              isEditModalOpen={isEditModalOpen}
              onCloseHandler={this.onEditFormCloseHandler}
              onSubmitHandler={this.onEditFormSubmitHandler}
              returnInvoice={selectedReturnInvoice}
              error={editFormError}
              submitting={editFormSubmitting}
            />
          </div>
        )}
        <Confirm
          open={isConfirmationOpen}
          onCancel={this.handleConfirmationClose}
          onConfirm={this.handleConfirm}
          error={resendFormError}
        />
      </div>
    );
  }
}

ReturnInvoices.propTypes = {
  clearSearchFields: PropTypes.func.isRequired,
  dispatchRefreshSearch: PropTypes.func.isRequired,
  refreshSearch: PropTypes.bool,
  data: PropTypes.shape({
    refetch: PropTypes.func.isRequired,
    fetchMore: PropTypes.func.isRequired,
    returnInvoices: PropTypes.array,
    variables: PropTypes.shape({
      productFamily: PropTypes.string,
    }).isRequired,
  }).isRequired,
  productFamily: PropTypes.string.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  resendVraMutation: PropTypes.func.isRequired,
  cancelMutation: PropTypes.func.isRequired,
  editMutation: PropTypes.func.isRequired,
  setSearchFields: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  searchFields: PropTypes.object,
  user: PropTypes.object,
};

const mapDispatchToProps = dispatch => ({
  setSearchFields: fields => {
    dispatch(setSearch(fields));
  },
  dispatchRefreshSearch: value => {
    dispatch(setRefreshSearch(value));
  },
  clearSearchFields: () => {
    dispatch(clearSearch());
  },
  setPage: () => {
    dispatch(setPage());
  },
});

const mapStateToProps = state => {
  const {
    data: { searchFields, user, refreshSearch },
  } = state;
  return {
    searchFields,
    user,
    refreshSearch,
    productFamily: selectReturnInvoicesProductFamily(state),
  };
};

export default compose(
  withRouter,
  withPagination(returnInvoicesQuery, 'returnInvoices', {}, state => ({
    productFamily: selectReturnInvoicesProductFamily(state),
  })),
  graphql(cancelReturnInvoiceMutation, { name: 'cancelMutation' }),
  graphql(updateReturnInvoiceMutation, { name: 'editMutation' }),
  graphql(resendVraStatusMutation, { name: 'resendVraMutation' }),
  LoadingDataEnhancer,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(ReturnInvoices);
